import { useWebAppGetFragmentDetailById } from '@mi-front/api-v2';
import { useMemo } from 'react';
import { getStablePosterUri } from '../utils/poster-cache';

export type PosterVariant = 'h180' | 'h360' | 'h720';

/**
 * Polls the BFF for a single fragment by id when poster is missing.
 * Returns the resolved poster URI for the given variant when the API returns poster data;
 * polling stops once poster is available. Uses getStablePosterUri for consistency with list cards.
 *
 * @param fragmentId - Fragment id to fetch (poll only when enabled and this is set).
 * @param variant - Poster size to resolve (h180, h360, h720). When null, h360 is used for the request.
 * @param enabled - When true and posterSrc from list is null, run the query and refetch until poster appears.
 * @returns Resolved poster URL for the variant, or null while loading or when no poster yet.
 */
export function useFragmentPosterPolling(
  fragmentId: string,
  variant: PosterVariant | null,
  enabled: boolean,
): string | null {
  const size = variant ?? 'h360';
  const shouldEnable = Boolean(fragmentId && enabled);

  const query = useWebAppGetFragmentDetailById(fragmentId, {
    query: {
      enabled: shouldEnable,
      refetchInterval: (q) =>
        q.state.data?.assets?.poster?.url && q.state.dataUpdateCount < 10 ? false : 5000,
    },
  });

  return useMemo(() => {
    const data = query.data;
    const posterUri = data?.assets?.poster?.url;

    if (!fragmentId || !posterUri) {
      return null;
    }

    return getStablePosterUri(fragmentId, size, posterUri);
  }, [fragmentId, size, query.data?.assets?.poster?.url]);
}
